import React from "react";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { BarChart } from "@mui/x-charts/BarChart";
import { startOfWeek, endOfWeek, eachDayOfInterval, format } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ResponsiveCharts from "../ChartsLayout/ResponsiveCharts.jsx";
import CustomGridItem from "../ChartsLayout/CustomGridItem.jsx";
import {
  BarChartEmpty,
  HiddenTypography,
} from "../ChartsLayout/EmptyCharts.jsx";

export default function HeartRate({
  heartRateData,
  heartRateRef,
  selectedWeek,
}) {
  const weekStarts = startOfWeek(selectedWeek, { weekStartsOn: 1 });
  const weekEnds = endOfWeek(selectedWeek, { weekStartsOn: 1 });

  const weekDays = eachDayOfInterval({
    start: weekStarts,
    end: weekEnds,
  });
  const { t } = useTranslation("statistics");

  // console.log("Heart rate data in heart rate component: ", heartRateData);

  const getHeartRateForADate = (date) => {
    const dateFormatted = format(date, "yyyy-MM-dd");
    const dayHeartRAteData = heartRateData.find(
      (data) => data.date === dateFormatted
    );
    return dayHeartRAteData
      ? {
          min: dayHeartRAteData.min,
          max: dayHeartRAteData.max,
          average: dayHeartRAteData.average,
          resting: dayHeartRAteData.resting,
        }
      : 0;
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ResponsiveCharts>
        {({ chartWidth }) => (
          <CustomGridItem>
            <div ref={heartRateRef}>
              <Typography
                sx={{
                  margin: {
                    xs: "20px 0",
                    sm: "20px 0",
                    md: "0 0 20px 0",
                    lg: "20px 0 0 20px",
                    xl: "20px",
                  },
                  textAlign: "center",
                }}
                variant="h6"
              >
                {t("charts.heartRate.overviewTitle")}
              </Typography>
              {weekDays.some((day) => {
                const data = getHeartRateForADate(day);
                return data && Object.values(data).some((val) => val > 0);
              }) ? (
                <>
                  <HiddenTypography />
                  <BarChart
                    xAxis={[
                      {
                        scaleType: "band",
                        data: weekDays.map((day) => format(day, "EEE")),
                        padding: 0.2,
                      },
                    ]}
                    series={[
                      {
                        data: weekDays.map(
                          (day) => getHeartRateForADate(day).resting
                        ),
                        label: t("series.restingBpm"),
                      },
                      {
                        data: weekDays.map(
                          (day) => getHeartRateForADate(day).min
                        ),
                        label: t("series.minBpm"),
                      },
                      {
                        data: weekDays.map(
                          (day) => getHeartRateForADate(day).average
                        ),
                        label: t("series.avgBpm"),
                      },
                      {
                        data: weekDays.map(
                          (day) => getHeartRateForADate(day).max
                        ),
                        label: t("series.maxBpm"),
                      },
                    ]}
                    width={chartWidth}
                    height={300}
                    slotProps={{
                      legend: {
                        direction: "row",
                        position: { vertical: "top", horizontal: "middle" },
                        padding: -5,
                        itemMarkWidth: 20,
                        itemMarkHeight: 3,
                        markGap: 5,
                        itemGap: 5,
                      },
                    }}
                  />
                </>
              ) : (
                <BarChartEmpty weekDays={weekDays} chartWidth={chartWidth} />
              )}
            </div>
          </CustomGridItem>
        )}
      </ResponsiveCharts>
    </LocalizationProvider>
  );
}
